import React, { useState } from "react";
import {
  DatePicker as AntDatePicker,
  DatePickerProps as AntDatePickerProps,
} from "antd";
import { RangePickerProps } from "antd/es/date-picker";
import type { Dayjs } from "dayjs";
import { Calendar, Clock, X } from "lucide-react";

const { RangePicker: AntRangePicker } = AntDatePicker;

export interface CustomDatePickerProps
  extends Omit<AntDatePickerProps, "suffixIcon" | "clearIcon" | "variant"> {
  label?: string;
  error?: string;
  required?: boolean;
  allowClear?: boolean;
  helperText?: string;
  variant?: "default" | "outlined" | "filled";
  customSuffixIcon?: React.ReactNode;
  customClearIcon?: React.ReactNode;
  showTime?: boolean;
  showToday?: boolean;
  disabledDates?: (date: Dayjs) => boolean;
  highlightDates?: string[]; // Array of dates to highlight
  maxDate?: Dayjs;
  minDate?: Dayjs;
}

export interface CustomRangePickerProps
  extends Omit<RangePickerProps, "suffixIcon" | "clearIcon" | "variant"> {
  label?: string;
  error?: string;
  required?: boolean;
  helperText?: string;
  variant?: "default" | "outlined" | "filled";
  customSuffixIcon?: React.ReactNode;
  customClearIcon?: React.ReactNode;
  maxRange?: number; // Maximum number of days that can be selected
  minRange?: number; // Minimum number of days that must be selected
  disabledDates?: (date: Dayjs) => boolean;
  highlightDates?: string[];
  maxDate?: Dayjs;
  minDate?: Dayjs;
}

const handleDateKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
  const allowedKeys = [
    "Backspace",
    "Delete",
    "Tab",
    "Escape",
    "Enter",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Home",
    "End",
  ];

  if (allowedKeys.includes(e.key)) return;

  if (e.ctrlKey || e.metaKey) return;

  if (/^[0-9\/\-:]$/.test(e.key)) return;

  e.preventDefault();
};

// Single DatePicker Component
const DatePicker: React.FC<CustomDatePickerProps> = ({
  label,
  error,
  required,
  allowClear = true,
  helperText,
  variant = "default",
  customSuffixIcon,
  customClearIcon,
  showTime = false,
  showToday = true,
  disabledDates,
  highlightDates = [],
  maxDate,
  minDate,
  className = "",
  style = {},
  ...antdProps
}) => {
  const [focused, setFocused] = useState(false);
  const isDisabled = antdProps.disabled;

  const getVariantStyles = () => {
    const base = {
      borderRadius: "11px",
      border: error
        ? "1px solid var(--color-error)"
        : focused
          ? "1px solid var(--color-border-focus)"
          : "1px solid var(--color-border-primary)",
      backgroundColor: error
        ? "var(--color-bg-base)"
        : isDisabled
          ? "var(--color-bg-disabled)"
          : "var(--color-bg-muted)",
      boxShadow: error
        ? "0 0 0 3px var(--color-error-light)"
        : focused
          ? "0 0 0 3px rgba(109,40,217,0.14)"
          : "none",
      transition: "all 0.2s",
    };

    if (variant === "outlined") {
      return {
        ...base,
        border: "2px solid var(--color-border-input-hover)",
        backgroundColor: "var(--color-bg-hover)",
      };
    }
    if (variant === "filled") {
      return { ...base, backgroundColor: "var(--color-bg-disabled)" };
    }
    return base;
  };

  const handleDisabledDate = (date: Dayjs) => {
    if (disabledDates && disabledDates(date)) return true;
    if (maxDate && date.isAfter(maxDate)) return true;
    if (minDate && date.isBefore(minDate)) return true;
    return false;
  };

  const variantStyles = getVariantStyles();

  return (
    <div className="custom-date-picker-wrapper">
      {label && (
        <label className="block text-xs font-semibold text-[var(--color-text-primary)] mb-[7px]">
          {label}
          {required && <span className="text-[var(--color-error)] ml-1">*</span>}
        </label>
      )}

      <AntDatePicker
        showTime={showTime}
        format={antdProps.format || "DD/MM/YYYY"}
        showToday={showToday}
        disabledDate={handleDisabledDate}
        allowClear={
          allowClear
            ? { clearIcon: customClearIcon || <X size={14} /> }
            : false
        }
        suffixIcon={
          customSuffixIcon ||
          (showTime ? <Clock size={16} /> : <Calendar size={16} />)
        }
        placeholder={
          antdProps.placeholder || `Select ${showTime ? "date & time" : "date"}`
        }
        onKeyDown={handleDateKeyDown}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        {...antdProps}
        className={`custom-date-picker ${className}`}
        style={{
          width: "100%",
          height: "44px",
          ...variantStyles,
          ...style,
        }}
      />

      {(error || helperText) && (
        <div className="mt-1">
          {error ? (
            <p className="text-xs text-[var(--color-error)] flex items-center gap-1">
              <X size={14} />
              {error}
            </p>
          ) : (
            <p className="text-xs text-[var(--color-text-tertiary)]">{helperText}</p>
          )}
        </div>
      )}
    </div>
  );
};

// Range DatePicker Component
const RangePicker: React.FC<CustomRangePickerProps> = ({
  label,
  error,
  required,
  helperText,
  variant = "default",
  customSuffixIcon,
  customClearIcon,
  maxRange,
  minRange,
  disabledDates,
  highlightDates = [],
  maxDate,
  minDate,
  className = "",
  style = {},
  ...antdProps
}) => {
  const [focused, setFocused] = useState(false);

  const getVariantStyles = () => {
    const base = {
      borderRadius: "11px",
      border: error
        ? "1px solid var(--color-error)"
        : focused
          ? "1px solid var(--color-border-focus)"
          : "1px solid var(--color-border-primary)",
      backgroundColor: error
        ? "var(--color-bg-base)"
        : "var(--color-bg-muted)",
      boxShadow: error
        ? "0 0 0 3px var(--color-error-light)"
        : focused
          ? "0 0 0 3px rgba(109,40,217,0.14)"
          : "none",
      transition: "all 0.2s",
    };

    if (variant === "outlined") {
      return {
        ...base,
        border: "2px solid var(--color-border-input-hover)",
        backgroundColor: "var(--color-bg-hover)",
      };
    }
    if (variant === "filled") {
      return { ...base, backgroundColor: "var(--color-bg-disabled)" };
    }
    return base;
  };

  const handleDisabledDate = (date: Dayjs) => {
    if (disabledDates && disabledDates(date)) return true;
    if (maxDate && date.isAfter(maxDate)) return true;
    if (minDate && date.isBefore(minDate)) return true;
    return false;
  };

  const handleCalendarChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (!dates || !dates[0] || !dates[1]) return;

    const [start, end] = dates;
    const diffDays = end.diff(start, "day") + 1;

    if (maxRange && diffDays > maxRange) {
      console.warn(
        `Selected range (${diffDays} days) exceeds maximum allowed (${maxRange} days)`
      );
    }

    if (minRange && diffDays < minRange) {
      console.warn(
        `Selected range (${diffDays} days) is less than minimum required (${minRange} days)`
      );
    }
  };

  const variantStyles = getVariantStyles();

  return (
    <div className="custom-range-picker-wrapper">
      {label && (
        <label className="block text-xs font-semibold text-[var(--color-text-primary)] mb-[7px]">
          {label}
          {required && <span className="text-[var(--color-error)] ml-1">*</span>}
        </label>
      )}

      <AntRangePicker
        {...antdProps}
        format="DD/MM/YYYY"
        disabledDate={handleDisabledDate}
        onCalendarChange={handleCalendarChange}
        onKeyDown={handleDateKeyDown}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        suffixIcon={customSuffixIcon || <Calendar size={16} />}
        allowClear={{ clearIcon: customClearIcon || <X size={14} /> }}
        className={`
          custom-range-picker
          ${error
            ? "border-red-500 focus:border-red-500 focus:ring-red-500"
            : ""
          }
          ${className}
        `}
        style={{
          width: "100%",
          height: "44px",
          ...variantStyles,
          ...style,
        }}
      />

      {(maxRange || minRange) && (
        <div className="mt-1 text-xs text-[var(--color-text-tertiary)]">
          {minRange && maxRange && (
            <span>
              Select between {minRange} and {maxRange} days
            </span>
          )}
          {minRange && !maxRange && (
            <span>Select at least {minRange} days</span>
          )}
          {!minRange && maxRange && <span>Select up to {maxRange} days</span>}
        </div>
      )}

      {(error || helperText) && (
        <div className="mt-1">
          {error ? (
            <p className="text-xs text-[var(--color-error)] flex items-center gap-1">
              <X size={14} />
              {error}
            </p>
          ) : (
            <p className="text-xs text-[var(--color-text-tertiary)]">{helperText}</p>
          )}
        </div>
      )}
    </div>
  );
};

// Export both components
export { DatePicker, RangePicker };
export default DatePicker;
